import { useState, useEffect } from 'react';
import { Trash2, Package, CreditCard, User, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { ordersService } from '@/api/services/orders';
import { productsService } from '@/api/services/products';
import { formatCurrency } from '@/utils/currency';
import type { Order, ApiOrder, OrderItem, CreateOrderRequest, Address, Product } from '../types';
import { toast } from 'sonner';

interface OrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId?: string | null;
    onOrderCreated?: (order: Order) => void;
    onOrderUpdated?: (order: Order) => void;
}

export function OrderModal({ isOpen, onClose, orderId, onOrderCreated, onOrderUpdated }: OrderModalProps) {
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState<ApiOrder | null>(null);
    const [items, setItems] = useState<OrderItem[]>([]);
    const [availableProducts, setAvailableProducts] = useState<Product[]>([]);

    // Form state for creating new order
    const [formData, setFormData] = useState<Partial<CreateOrderRequest>>({
        currency: 'USD',
        status: 'NEW',
        customer_name: '',
        customer_email: '',
        shipping_address: {
            line1: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US'
        },
        billing_address: {
            line1: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US'
        },
        items: [],
        subtotal_amount: 0,
        tax_amount: 0,
        shipping_amount: 0,
        discount_amount: 0,
        total_amount: 0,
    });

    useEffect(() => {
        if (isOpen) {
            if (orderId) {
                fetchOrderDetail(orderId);
            } else {
                setOrder(null);
                setItems([]);
                fetchProducts();
            }
        }
    }, [isOpen, orderId]);

    const fetchOrderDetail = async (id: string) => {
        setLoading(true);
        try {
            const data = await ordersService.getOrder(id);
            setOrder(data.order);
            setItems(data.items);
        } catch (error) {
            console.error('Failed to fetch order detail:', error);
            toast.error('Failed to fetch order details');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const data = await productsService.getProducts();
            setAvailableProducts(data.products);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        }
    };

    const addItem = (product: Product) => {
        const newItem: OrderItem = {
            id: crypto.randomUUID(),
            order_id: '',
            product_id: product.id,
            sku: product.sku || '',
            name: product.name,
            quantity: 1,
            unit_price: product.price,
            tax_amount: 0,
            discount_amount: 0,
            total_amount: product.price,
        };
        const newItems = [...items, newItem];
        setItems(newItems);
        calculateTotals(newItems);
    };

    const removeItem = (id: string) => {
        const newItems = items.filter(item => item.id !== id);
        setItems(newItems);
        calculateTotals(newItems);
    };

    const updateItemQuantity = (id: string, quantity: number) => {
        const newItems = items.map(item => {
            if (item.id === id) {
                const q = Math.max(1, quantity);
                return {
                    ...item,
                    quantity: q,
                    total_amount: q * item.unit_price
                };
            }
            return item;
        });
        setItems(newItems);
        calculateTotals(newItems);
    };

    const calculateTotals = (currentItems: OrderItem[]) => {
        const subtotal = currentItems.reduce((sum, item) => sum + item.total_amount, 0);
        const tax = subtotal * 0.08; // Example tax
        const total = subtotal + tax + (formData.shipping_amount || 0) - (formData.discount_amount || 0);

        setFormData(prev => ({
            ...prev,
            subtotal_amount: subtotal,
            tax_amount: tax,
            total_amount: total
        }));
    };

    const handleSubmit = async () => {
        if (!formData.customer_email || items.length === 0) {
            toast.error('Please fill in customer email and add at least one item');
            return;
        }

        setLoading(true);
        try {
            const request: CreateOrderRequest = {
                idempotency_key: crypto.randomUUID(),
                currency: formData.currency!,
                subtotal_amount: formData.subtotal_amount!,
                tax_amount: formData.tax_amount!,
                shipping_amount: formData.shipping_amount || 0,
                discount_amount: formData.discount_amount || 0,
                total_amount: formData.total_amount!,
                status: formData.status!,
                customer_name: formData.customer_name,
                customer_email: formData.customer_email,
                shipping_address: formData.shipping_address as Address,
                billing_address: formData.billing_address as Address,
                items: items,
            };

            const createdOrder = await ordersService.createOrder(request);
            toast.success('Order created successfully');
            onOrderCreated?.(createdOrder);
            onClose();
        } catch (error) {
            console.error('Failed to create order:', error);
            toast.error('Failed to create order');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{order ? `Order ${order.external_order_id || order.id.slice(0, 8).toUpperCase()}` : 'Create New Order'}</DialogTitle>
                </DialogHeader>

                {loading && !order ? (
                    <div className="flex justify-center p-8">Loading...</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <User className="w-4 h-4" /> Customer Information
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {order ? (
                                        <>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Name</p>
                                                <p className="font-medium">{order.customer_name}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Email</p>
                                                <p className="font-medium">{order.customer_email}</p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Email</label>
                                                <Input
                                                    placeholder="customer@example.com"
                                                    value={formData.customer_email || ''}
                                                    onChange={e => setFormData({ ...formData, customer_email: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Name</label>
                                                <Input
                                                    placeholder="Customer Name"
                                                    value={formData.customer_name || ''}
                                                    onChange={e => setFormData({ ...formData, customer_name: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <MapPin className="w-4 h-4" /> Shipping Address
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {order ? (
                                        <p className="text-sm">
                                            {/* Address display for existing order */}
                                            Pending API expansion for detailed address retrieval
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="col-span-2 space-y-2">
                                                <label className="text-sm font-medium">Address</label>
                                                <Input
                                                    placeholder="Street Address"
                                                    value={formData.shipping_address?.line1}
                                                    onChange={e => setFormData({
                                                        ...formData,
                                                        shipping_address: { ...formData.shipping_address!, line1: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">City</label>
                                                <Input
                                                    placeholder="City"
                                                    value={formData.shipping_address?.city}
                                                    onChange={e => setFormData({
                                                        ...formData,
                                                        shipping_address: { ...formData.shipping_address!, city: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Postal Code</label>
                                                <Input
                                                    placeholder="Postal Code"
                                                    value={formData.shipping_address?.postal_code}
                                                    onChange={e => setFormData({
                                                        ...formData,
                                                        shipping_address: { ...formData.shipping_address!, postal_code: e.target.value }
                                                    })}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <Package className="w-4 h-4" /> Order Items
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {!order && (
                                        <div className="mb-4">
                                            <Select onValueChange={(val) => {
                                                const product = availableProducts.find(p => p.id === val);
                                                if (product) addItem(product);
                                            }}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Add a product..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {availableProducts.map(p => (
                                                        <SelectItem key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Item</TableHead>
                                                <TableHead>Qty</TableHead>
                                                <TableHead>Total</TableHead>
                                                {!order && <TableHead></TableHead>}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {items.map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell className="text-xs font-medium">{item.name}</TableCell>
                                                    <TableCell>
                                                        {order ? item.quantity : (
                                                            <Input
                                                                type="number"
                                                                className="w-16 h-8"
                                                                value={item.quantity}
                                                                onChange={e => updateItemQuantity(item.id, parseInt(e.target.value))}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs">{formatCurrency(item.total_amount)}</TableCell>
                                                    {!order && (
                                                        <TableCell>
                                                            <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)}>
                                                                <Trash2 className="w-4 h-4 text-destructive" />
                                                            </Button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    <div className="mt-4 pt-4 border-t space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Subtotal</span>
                                            <span>{formatCurrency(order?.total_amount || formData.subtotal_amount || 0)}</span>
                                        </div>
                                        {!order && (
                                            <div className="flex justify-between text-sm">
                                                <span>Tax (8%)</span>
                                                <span>{formatCurrency(formData.tax_amount || 0)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between font-bold">
                                            <span>Total</span>
                                            <span>{formatCurrency(order?.total_amount || formData.total_amount || 0)}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {order && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                                            <CreditCard className="w-4 h-4" /> Order Status
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex items-center gap-2">
                                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'DELIVERED' ? 'bg-green-100 text-green-700' :
                                                order.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                                                    'bg-blue-100 text-blue-700'
                                                }`}>
                                                {order.status}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                    {!order && (
                        <Button onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Creating...' : 'Create Order'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
